import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axios";
import useAuth from "../hooks/useAuth";
import "./schoolEmail.css";

const SchoolEmail = ({ onVerified, children }) => {
  const { authState } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.hash.slice(1)).get("token");
  const [email, setEmail] = useState("");
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (verified && token) {
      navigate({ pathname: location.pathname, search: location.search, hash: "" }, { replace: true });
    }
  }, [verified, token, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!authState.isAuthenticated || busy || verified) return;
    const checkStatus = () => setRefresh((value) => value + 1);
    window.addEventListener("focus", checkStatus);
    return () => window.removeEventListener("focus", checkStatus);
  }, [authState.isAuthenticated, busy, verified]);

  useEffect(() => {
    if (!authState.isAuthenticated) return;
    let active = true;
    setLoading(true);
    axiosInstance.get("/api/school-email").then(({ data }) => {
      if (!active) return;
      if (data.verified) {
        setEmail(data.email);
        setError("");
      }
      setVerified(data.verified);
      onVerified?.(data.verified);
      setLoading(false);
    }).catch(() => {
      if (!active) return;
      setError("Could not load school email status. Reload this page to try again.");
      setLoading(false);
    });
    return () => { active = false; };
  }, [authState.isAuthenticated, onVerified, refresh]);

  const sendLink = async (event) => {
    event.preventDefault();
    if (["gmail.com", "googlemail.com"].includes(email.trim().toLowerCase().split("@").pop().replace(/\.+$/, ""))) {
      setMessage("");
      setError("Use your school-issued email address instead of Gmail");
      return;
    }
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const { data } = await axiosInstance.post("/api/school-email", { email });
      if (data.verified) {
        setEmail(data.email);
        setVerified(true);
        onVerified?.(true);
      } else {
        setMessage(data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Could not send the link. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const verifyLink = async () => {
    setBusy(true);
    setError("");
    try {
      await axiosInstance.post("/api/school-email/verify", { token });
      // Reload the server's verified address, rather than trusting the form value.
      const { data } = await axiosInstance.get("/api/school-email");
      setEmail(data.email);
      setVerified(data.verified);
      onVerified?.(data.verified);
      if (!data.verified) {
        setError("Verification is not complete. Try this link again or request a new link.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Could not verify this link. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="school-email-card" aria-label="School email verification" aria-busy={busy || (loading && authState.isAuthenticated)}>
        <h3>School email</h3>
        {!authState.isAuthenticated ? (
          <p><Link to="/login" state={{ from: location }}>Sign in</Link> with the account that requested this link to verify your school email.</p>
        ) : loading ? (
          <p role="status">Loading school email status...</p>
        ) : verified ? (
          <>
            <p role="status">School email verified: <strong>{email}</strong></p>
            {location.pathname === "/school-email" && <Link to="/dashboard">Return to dashboard</Link>}
          </>
        ) : (
          <>
            {token ? (
              <div className="school-email-link-action">
                <p>One more step: click "Verify school email" below to finish. You do not need to enter your school email again.</p>
                <p>Signed in as {authState.user?.email}. Use the account that requested this link.</p>
                <button type="button" disabled={busy} onClick={verifyLink}>{busy ? "Verifying..." : "Verify school email"}</button>
                <p>Use the most recent verification email. Links expire after 30 minutes. If this link has expired, enter your school email again and select "Send verification link" to receive a new email.</p>
                <Link to={{ pathname: location.pathname, search: location.search, hash: "" }} replace
                  aria-disabled={busy} onClick={(event) => {
                    if (busy) {
                      event.preventDefault();
                      return;
                    }
                    setError("");
                    setMessage("");
                  }}>Enter school email for a new link</Link>
              </div>
            ) : (
              <>
                <p>Enter your school-issued email and verify it using the link we send. This is required before you submit an application or confirm attendance. Your login email will not change.</p>
                <p>Enter your school email below and select "Send verification link". Then open the newest email and select "Verify school email". Sending a new link makes previous links invalid.</p>
                <form onSubmit={sendLink}>
                  <label htmlFor="school-email-address">School email address</label>
                  <input id="school-email-address" name="school_email" type="email" required maxLength={254}
                    autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} />
                  <button type="submit" disabled={busy}>{busy ? "Please wait..." : "Send verification link"}</button>
                </form>
                <p className="school-email-note">No .edu address is needed. Use your school-issued address, including international school domains. Gmail addresses are not accepted. After verification, contact the organizers if you need to change it.</p>
              </>
            )}
          </>
        )}
        {message && <p role="status">{message}</p>}
        {error && <p role="alert">{error}</p>}
      </section>
      {verified && children}
    </>
  );
};

export default SchoolEmail;
